/**
 * The donor door, as a page.
 *
 * Shown instead of the application whenever the browser is at the donor hostname and
 * has no session — App renders this in place of its Zero Trust notice, which is the
 * equivalent screen for the other door.
 *
 * Three screens in one file, chosen by the path, because they are three steps of one
 * errand and every one of them can hand off to another: signing in offers the
 * forgotten-password form, that form sends a link, and the link lands on the
 * set-password form. Splitting them would put the shared frame in a fourth file.
 *
 * Nothing here decides anything. Every rule this page appears to enforce — how long a
 * password must be, whether a link is still good, whether an address is one we know —
 * is enforced by the Worker, and is repeated here only so somebody is told before they
 * have finished typing rather than after.
 *
 * There is no way to create an account from this page, and no link to one, because
 * there is no endpoint behind it. A donor account exists because somebody with a staff
 * account made one.
 */
import { useState, useEffect, createContext, useContext } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Notice, Field, Spinner, Loading } from '../components/ui'
import { ThemeCycleButton } from '../lib/theme'
import Turnstile from '../components/Turnstile'
import {
  donorSignIn, donorForgot, donorPeekToken, donorSetPassword, PASSWORD_MIN,
} from '../lib/donor'

/**
 * The challenge, on the two forms a stranger can reach.
 *
 * Not on the set-password screen below it. That one is already behind a link nobody can
 * guess — 32 bytes of CSPRNG, single use, checked before the form is even drawn — so a
 * widget there would ask a donor accepting an invitation to prove something the link has
 * proved already. What the challenge is for is the two forms that answer to anybody who
 * types an address: sign-in, where a wrong guess still costs this Worker a full password
 * hash, and forgotten-password, which sends mail.
 *
 * `sitekey` is null whenever the deployment has no Turnstile keys, and every piece of
 * this then renders nothing and submits undefined, which the Worker accepts because it
 * is not checking either. There is no separate "is it on" flag anywhere.
 */
function useChallenge(sitekey) {
  const [token, setToken] = useState(null)
  // Bumped after every attempt that came back. A token is spent once it has been sent,
  // so the widget has to be told to mint another before the visitor can try again.
  const [resetKey, setResetKey] = useState(0)
  return {
    token,
    setToken,
    spend: () => setResetKey((n) => n + 1),
    // Nothing to wait for when the challenge is off; otherwise the button stays out of
    // reach until the widget has produced something to send.
    ready: !sitekey || !!token,
    widget: (action) => (
      <Turnstile sitekey={sitekey} action={action} onToken={setToken} resetKey={resetKey} />
    ),
  }
}

/**
 * The frame the three screens share: the wordmark, a card, and the appearance control.
 *
 * Deliberately narrow and vertically centred rather than laid out like a page of the
 * app. Somebody arriving here is not reading the archive yet, and a sign-in form that
 * fills a screen implies there is more to do on it than there is.
 */
/**
 * What this installation is called, for the four screens below that draw the heading.
 *
 * A context rather than a prop through every Frame: the name is one fact that arrives
 * once with the door's context and is read at the leaves, which is what a context is
 * for. Threading it would touch six call sites and mean nothing at any of them.
 */
const OrgCtx = createContext(null)

function Frame({ title, sub, children }) {
  const orgName = useContext(OrgCtx)
  return (
    <div className="page" style={{ maxWidth: 460, margin: '10vh auto' }}>
      <div className="col">
        <div>
          {/* The context request that tells this page whether to draw a challenge
              widget also carries the name, so both doors are renamed together. */}
          <h1 style={{ marginBottom: 4 }}>{orgName || 's4narchive'}</h1>
          <div className="subtle small">Updates &amp; partner unit records</div>
        </div>
        <div className="card">
          <div className="card-body col">
            <div>
              <h2 style={{ margin: 0, fontSize: 17 }}>{title}</h2>
              {sub && (
                <div className="small subtle" style={{ marginTop: 4 }}>
                  {sub}
                </div>
              )}
            </div>
            {children}
          </div>
        </div>
        <div className="row">
          <ThemeCycleButton />
        </div>
      </div>
    </div>
  )
}

export default function DonorSignIn({ ctx }) {
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const token = params.get('token')
  const sitekey = ctx?.turnstileSiteKey ?? null

  // A link that carries a token is the set-password screen wherever it landed, so a
  // stale bookmark of `/prayer-requests?token=…` still works.
  const screen = token ? (
    <SetPassword token={token} />
  ) : pathname === '/forgot' ? (
    <Forgot sitekey={sitekey} />
  ) : (
    <SignIn sitekey={sitekey} />
  )
  return <OrgCtx.Provider value={ctx?.orgName ?? null}>{screen}</OrgCtx.Provider>
}

/**
 * Landing back in the archive after the cookie is set.
 *
 * A whole-page load rather than a re-render: it is the simplest way to have every
 * query refetched under the new identity, and it takes the one-time token out of the
 * address bar and out of the session history on the way past.
 */
const enterArchive = () => window.location.replace('/')

function SignIn({ sitekey }) {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const challenge = useChallenge(sitekey)

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await donorSignIn(email, password, challenge.token)
      enterArchive()
    } catch (err) {
      setError(err?.message || 'Could not sign in.')
      // Only on the failure path: the success path leaves this page entirely, and
      // resetting a widget on the way out is work nobody sees.
      challenge.spend()
      setBusy(false)
    }
  }

  return (
    <Frame title="Sign in" sub="For supporters with a view-only account">
      <form className="col" onSubmit={submit}>
        {error && (
          <Notice tone="danger" icon="warning">
            {error}
          </Notice>
        )}
        <Field label="Email" htmlFor="donor-email">
          <input
            id="donor-email"
            className="input"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password" htmlFor="donor-password">
          <input
            id="donor-password"
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {challenge.widget('donor-signin')}
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="btn btn-primary" type="submit" disabled={busy || !challenge.ready}>
            {busy ? <Spinner /> : 'Sign in'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/forgot')}
          >
            Forgotten your password?
          </button>
        </div>
      </form>
      {/* Said plainly rather than left to be discovered by looking for a link that is
          not there. Someone who cannot sign in needs to know who to ask. */}
      <p className="small subtle" style={{ margin: 0 }}>
        Accounts are created for you — there is no sign-up. If you do not have one, ask the person
        who sent you here.
      </p>
    </Frame>
  )
}

function Forgot({ sitekey }) {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState(null)
  const challenge = useChallenge(sitekey)

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await donorForgot(email, challenge.token)
      setDone(true)
    } catch (err) {
      /*
       * Two kinds of failure, and only one of them gets its own screen.
       *
       * A refused or unavailable challenge is the Worker talking about *this request* —
       * assertHuman() runs before the address is looked up and cannot know whether it is
       * one of ours — so saying so plainly gives nothing away, and staying quiet would
       * strand somebody on "check your email" for a letter that was never sent.
       *
       * Anything else keeps the old behaviour, which is the point of the endpoint: it
       * answers the same way whatever happens, and a transport failure is not worth a
       * different screen either, because the message below is true either way.
       */
      if (err?.status === 403 || err?.status === 503) {
        setError(err.message || 'Could not send that link.')
        challenge.spend()
      } else {
        setDone(true)
      }
    }
    setBusy(false)
  }

  /*
   * One confirmation, whether or not that address has an account here.
   *
   * The wording matters as much as the endpoint's silence does: "if there is an account"
   * is the honest phrasing, where "we have sent you an email" would be a claim this
   * screen is not in a position to make, and would quietly confirm the address to
   * anybody typing other people's.
   */
  if (done) {
    return (
      <Frame title="Check your email">
        <Notice icon="mail">
          If there is an account for <strong>{email}</strong>, a link to choose a new password is on
          its way. It works once and expires in an hour.
        </Notice>
        <p className="small subtle" style={{ margin: 0 }}>
          Nothing arrived? Check the spam folder, or ask the person who set up your account — they
          can send you a link directly.
        </p>
        <div className="row">
          <button className="btn" onClick={() => navigate('/')}>
            Back to sign in
          </button>
        </div>
      </Frame>
    )
  }

  return (
    <Frame title="Forgotten your password?" sub="We will email you a link to choose a new one">
      <form className="col" onSubmit={submit}>
        {error && (
          <Notice tone="danger" icon="warning">
            {error}
          </Notice>
        )}
        <Field label="Email" htmlFor="forgot-email">
          <input
            id="forgot-email"
            className="input"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {challenge.widget('donor-forgot')}
        <div className="row">
          <button className="btn btn-primary" type="submit" disabled={busy || !challenge.ready}>
            {busy ? <Spinner /> : 'Send me a link'}
          </button>
          <button type="button" className="btn" onClick={() => navigate('/')}>
            Cancel
          </button>
        </div>
      </form>
    </Frame>
  )
}

/**
 * The far end of an invitation or a reset.
 *
 * The link is looked at before anything is asked of the person holding it — that is
 * what `/api/donor/token` is for. Taking a password first and only then admitting the
 * link had expired is the version of this screen that wastes somebody's time and makes
 * them think they have chosen a password when they have not.
 */
function SetPassword({ token }) {
  const navigate = useNavigate()
  const [peek, setPeek] = useState(null)
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    donorPeekToken(token)
      .then((r) => live && setPeek(r))
      .catch(() => live && setPeek({ valid: false }))
    return () => {
      live = false
    }
  }, [token])

  if (!peek) {
    return (
      <Frame title="Checking your link…">
        <Loading rows={2} label="One moment" />
      </Frame>
    )
  }

  if (!peek.valid) {
    return (
      <Frame title="That link has expired">
        <Notice tone="warn" icon="pending">
          Links can only be used once, and an invitation lasts seven days — a reset, an hour. Ask
          for a new one and it will be emailed to you.
        </Notice>
        <div className="row">
          <button className="btn btn-primary" onClick={() => navigate('/forgot')}>
            Send me a new link
          </button>
          <button className="btn" onClick={() => navigate('/')}>
            Back to sign in
          </button>
        </div>
      </Frame>
    )
  }

  const tooShort = password.length > 0 && password.length < PASSWORD_MIN
  const mismatch = again.length > 0 && password !== again
  const ready = password.length >= PASSWORD_MIN && password === again

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await donorSetPassword(token, password)
      // Setting the password signs them in, so there is nothing left to type.
      enterArchive()
    } catch (err) {
      setError(err?.message || 'Could not set that password.')
      setBusy(false)
    }
  }

  const invited = peek.kind === 'invite'

  return (
    <Frame
      title={invited ? 'Choose a password' : 'Choose a new password'}
      sub={peek.name ? `for ${peek.name} — ${peek.email}` : peek.email}
    >
      {invited && (
        <Notice icon="person">
          Welcome. This account lets you read the updates and prayer requests from our partners.
          Choose a password and you are in.
        </Notice>
      )}
      <form className="col" onSubmit={submit}>
        {error && (
          <Notice tone="danger" icon="warning">
            {error}
          </Notice>
        )}
        {/* The address, as a field the browser can see but nobody can edit. Password
            managers key what they save on the username in the form, and with no such
            field they either save nothing or guess. */}
        <input type="hidden" autoComplete="username" value={peek.email} readOnly />
        <Field
          label="New password"
          htmlFor="new-password"
          hint={`At least ${PASSWORD_MIN} characters. A few ordinary words you will remember beat one short and clever one.`}
          error={tooShort ? `That is shorter than ${PASSWORD_MIN} characters.` : null}
        >
          <input
            id="new-password"
            className="input"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field
          label="Type it again"
          htmlFor="confirm-password"
          error={mismatch ? 'These two do not match.' : null}
        >
          <input
            id="confirm-password"
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        </Field>
        <div className="row">
          <button className="btn btn-primary" type="submit" disabled={busy || !ready}>
            {busy ? <Spinner /> : invited ? 'Set my password and sign in' : 'Save and sign in'}
          </button>
        </div>
      </form>
    </Frame>
  )
}
